import { CheckIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { PlatformIcon } from '@/components/PlatformIcon';
import { getWeblinkDestination } from '@/features/interactions/addToCalendar/destinations/registry';
import { Box, Flex, Link, MotionFlex, Text } from '@/ui';
import {
  type CalendarFeedPlatform,
  getOfficeUrl,
  getOutlookUrl,
  getWebcalUrl,
  googleCalendarAddByUrlPage,
} from '../calendarFeedPlatform.lib';

type Props = {
  platform: CalendarFeedPlatform;
  url: string;
  isLastClicked: boolean;
  /** Narrow-surface rendering — see CalendarFeedPlatformGrid's isCompact. */
  isCompact?: boolean;
  onClick: () => void;
};

export const PlatformButton = ({
  platform,
  url,
  isLastClicked,
  isCompact = false,
  onClick,
}: Props) => {
  const { t } = useTranslation('calendarFeedPlatformGrid');
  const [didCopy, setDidCopy] = useState(false);
  // The feed vocabulary IS the weblink destinations' url-key vocabulary, so
  // the icon comes straight off the destination module.
  const destination = getWeblinkDestination(platform);

  const isCopyPlatform = platform === 'google' || platform === 'other';
  const showInstructions = platform === 'google';

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (didCopy) {
      const timeoutLength = showInstructions ? 10000 : 2000;
      timer = setTimeout(() => setDidCopy(false), timeoutLength);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [didCopy, showInstructions]);

  useEffect(() => {
    if (!isLastClicked) {
      setDidCopy(false);
    }
  }, [isLastClicked]);

  const getHref = () => {
    switch (platform) {
      case 'apple':
        return getWebcalUrl(url);
      case 'outlook':
        return getOutlookUrl(url);
      case 'office':
        return getOfficeUrl(url);
      default:
        return undefined;
    }
  };

  const getCopyUrl = () => {
    if (platform === 'google') return getWebcalUrl(url);
    return url;
  };

  const handleClick = async () => {
    if (isCopyPlatform && !didCopy) {
      try {
        await navigator.clipboard.writeText(getCopyUrl());
        setDidCopy(true);
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
      }
    }
    onClick();
  };

  const copiedContent = (
    <MotionFlex
      align="center"
      animate={{ opacity: 1 }}
      flexDirection="column"
      initial={{ opacity: 0 }}
      transition={{ duration: 0.1 }}
    >
      <Flex align="center" as="p" gap="3" m="0">
        <CheckIcon boxSize="3" /> {t('link_copied')}
      </Flex>
      {showInstructions && (
        <Flex
          as="p"
          flexWrap="wrap"
          fontSize="0.8rem"
          gap="1"
          justifyContent="center"
          m="0"
          textAlign="center"
        >
          <Trans
            i18nKey="paste_into_google"
            ns="calendarFeedPlatformGrid"
            components={{
              1: (
                <Link
                  color="blue.600"
                  href={googleCalendarAddByUrlPage}
                  isExternal
                  pointerEvents="auto"
                  textDecoration="underline"
                />
              ),
              2: <ExternalLinkIcon boxSize="3" />,
            }}
          />
        </Flex>
      )}
    </MotionFlex>
  );

  const defaultContent = (
    <>
      <Box>
        {destination && (
          <PlatformIcon
            platform={destination.platform}
            size={isCompact ? '16px' : '18px'}
          />
        )}
      </Box>
      {isCompact ? (
        // The eyebrow doubles the height and the section header above the
        // compact grid already says what these buttons do.
        <Text fontSize="ah-control" fontWeight="medium">
          {t(platform)}
        </Text>
      ) : (
        <Flex align="flex-start" direction="column">
          <Text
            color="customgray.mid"
            fontSize="xs"
            fontWeight="medium"
            textTransform="uppercase"
          >
            {t('send_to')}
          </Text>
          <Text fontWeight="medium">{t(platform)}</Text>
        </Flex>
      )}
    </>
  );

  const sharedStyles = {
    borderColor: 'customgray.gray',
    borderRadius: isCompact ? 'ah-md' : '6px',
    borderWidth: '1px',
    cursor: 'pointer' as const,
    display: 'flex',
    flex: '1',
    fontSize: isCompact ? 'ah-control' : undefined,
    gap: isCompact ? '2.5' : '6',
    p: isCompact ? '2.5' : '5',
  };

  if (isCopyPlatform) {
    return (
      <Flex
        _hover={didCopy ? { bg: 'green.50' } : { bg: 'gray.100' }}
        align="center"
        as="button"
        type="button"
        {...sharedStyles}
        {...(didCopy && {
          bg: 'green.50',
          justify: 'center',
          color: 'green.600',
          cursor: 'default',
          fontWeight: 'medium',
          pointerEvents: 'none',
        })}
        onClick={handleClick}
      >
        {didCopy ? copiedContent : defaultContent}
      </Flex>
    );
  }

  return (
    <Flex
      _hover={{ bg: 'gray.100', textDecor: 'none' }}
      align="center"
      as="a"
      href={getHref()}
      rel="noopener noreferrer"
      target="_blank"
      {...sharedStyles}
      onClick={handleClick}
    >
      {defaultContent}
    </Flex>
  );
};
